"use client";

import Link from 'next/link';
import { FileText, Mail, Calendar } from 'lucide-react';

export default function TermsAndConditions() {
  const effectiveDate = "January 1, 2025";
  const lastUpdated = "January 1, 2025";

  return (
    <main className="min-h-screen bg-gradient-to-b from-slate-50 to-white dark:from-slate-950 dark:to-slate-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 py-16 md:py-24">
        <div className="container mx-auto px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 shadow-xl shadow-amber-500/25 mb-6">
            <FileText className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Terms of Service
          </h1>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            Please read these terms carefully before using Easy Pairing
          </p>
          <div className="flex items-center justify-center gap-6 mt-6 text-sm text-slate-500">
            <span className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Effective: {effectiveDate}
            </span>
            <span className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Last Updated: {lastUpdated}
            </span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 lg:px-8 py-12 lg:py-16">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-slate dark:prose-invert prose-lg max-w-none">
            
            {/* Introduction */}
            <section className="mb-12">
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                Welcome to Easy Pairing! These Terms of Service (&quot;Terms&quot;) govern your access to and use of 
                the Easy Pairing platform, including our website, applications, and services (collectively, the &quot;Service&quot;). 
                By accessing or using our Service, you agree to be bound by these Terms.
              </p>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed mt-4">
                Easy Pairing is a team matching and compatibility platform that helps organizations, 
                educational institutions, and communities form optimal groups based on preferences, skills, and compatibility factors.
              </p>
            </section>

            {/* Section 1 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 text-sm font-bold">1</span>
                Acceptance of Terms
              </h2>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                By creating an account or using Easy Pairing, you acknowledge that you have read, understood, 
                and agree to be bound by these Terms and our{' '}
                <Link
                  href="/privacypolicy"
                  className="text-amber-600 dark:text-amber-400 hover:underline"
                >
                  Privacy Policy
                </Link>. If you do not agree to these Terms, you may not use our Service.
              </p>
            </section>

            {/* Section 2 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 text-sm font-bold">2</span>
                Eligibility
              </h2>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed mb-4">
                To use Easy Pairing, you must:
              </p>
              <ul className="list-disc list-inside text-slate-600 dark:text-slate-300 space-y-2 ml-4">
                <li>Be at least 18 years old, or the age of majority in your jurisdiction</li>
                <li>Have the legal capacity to enter into a binding agreement</li>
                <li>Not be prohibited from using the Service under applicable laws</li>
                <li>Provide accurate and complete registration information</li>
              </ul>
            </section>

            {/* Section 3 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 text-sm font-bold">3</span>
                Account Registration and Security
              </h2>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed mb-4">
                When you create an account, you agree to:
              </p>
              <ul className="list-disc list-inside text-slate-600 dark:text-slate-300 space-y-2 ml-4">
                <li>Provide accurate, current, and complete information</li>
                <li>Maintain the security and confidentiality of your login credentials</li>
                <li>Immediately notify us of any unauthorized access to your account</li>
                <li>Accept responsibility for all activities that occur under your account</li>
              </ul>
            </section>

            {/* Section 4 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 text-sm font-bold">4</span>
                Permitted Use
              </h2>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed mb-4">
                Easy Pairing grants you a limited, non-exclusive, non-transferable license to access and use 
                the Service for its intended purpose: facilitating team formation and compatibility matching 
                within organizations, educational settings, or community groups.
              </p>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                You agree to use the Service only for lawful purposes and in accordance with these Terms.
              </p>
            </section>

            {/* Section 5 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 text-sm font-bold">5</span>
                Prohibited Conduct
              </h2>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed mb-4">
                You agree not to:
              </p>
              <ul className="list-disc list-inside text-slate-600 dark:text-slate-300 space-y-2 ml-4">
                <li>Use the Service for any illegal or unauthorized purpose</li>
                <li>Violate any laws, regulations, or third-party rights</li>
                <li>Submit false, misleading, or inaccurate information</li>
                <li>Attempt to gain unauthorized access to the Service or its systems</li>
                <li>Interfere with or disrupt the Service or its infrastructure</li>
                <li>Use automated systems to access the Service without permission</li>
                <li>Harass, abuse, or harm other users</li>
                <li>Use the Service to discriminate against any protected class</li>
              </ul>
            </section>

            {/* Section 6 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 text-sm font-bold">6</span>
                User Content
              </h2>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed mb-4">
                You retain ownership of content you submit to Easy Pairing. By submitting content, you grant 
                us a non-exclusive, worldwide, royalty-free license to use, store, and process your content 
                solely for the purpose of providing and improving our Service.
              </p>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                You are solely responsible for your content and represent that you have all necessary rights 
                to submit it.
              </p>
            </section>

            {/* Section 7 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 text-sm font-bold">7</span>
                Subscription and Payment
              </h2>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed mb-4">
                Some features of Easy Pairing require a paid subscription. By subscribing, you agree to:
              </p>
              <ul className="list-disc list-inside text-slate-600 dark:text-slate-300 space-y-2 ml-4">
                <li>Pay all applicable fees as described at the time of purchase</li>
                <li>Automatic renewal of subscriptions unless cancelled before the renewal date</li>
                <li>Provide accurate billing information and maintain its accuracy</li>
              </ul>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed mt-4">
                Refunds are handled in accordance with our refund policy. You may cancel your subscription 
                at any time through your account settings.
              </p>
            </section>

            {/* Section 8 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 text-sm font-bold">8</span>
                Privacy and Data Protection
              </h2>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                Your privacy is important to us. Please review our{' '}
                <Link
                  href="/privacypolicy"
                  className="text-amber-600 dark:text-amber-400 hover:underline"
                >
                  Privacy Policy
                </Link>{' '}
                to understand how we collect, use, and protect your personal information. By using our 
                Service, you consent to our data practices as described in the Privacy Policy.
              </p>
            </section>

            {/* Section 9 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 text-sm font-bold">9</span>
                Intellectual Property
              </h2>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                Easy Pairing and its original content, features, and functionality are owned by Easy Pairing 
                and are protected by international copyright, trademark, patent, trade secret, and other 
                intellectual property laws. You may not copy, modify, distribute, or create derivative works 
                without our express written permission.
              </p>
            </section>

            {/* Section 10 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 text-sm font-bold">10</span>
                Disclaimer of Warranties
              </h2>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                THE SERVICE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; WITHOUT WARRANTIES OF ANY KIND, 
                EITHER EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, 
                ERROR-FREE, OR COMPLETELY SECURE. YOUR USE OF THE SERVICE IS AT YOUR OWN RISK.
              </p>
            </section>

            {/* Section 11 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 text-sm font-bold">11</span>
                Limitation of Liability
              </h2>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, EASY PAIRING SHALL NOT BE LIABLE FOR ANY INDIRECT, 
                INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, 
                WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER 
                INTANGIBLE LOSSES.
              </p>
            </section>

            {/* Section 12 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 text-sm font-bold">12</span>
                Indemnification
              </h2>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                You agree to indemnify, defend, and hold harmless Easy Pairing, its officers, directors, 
                employees, and agents from any claims, damages, losses, liabilities, and expenses (including 
                legal fees) arising from your use of the Service, your violation of these Terms, or your 
                violation of any rights of a third party.
              </p>
            </section>

            {/* Section 13 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 text-sm font-bold">13</span>
                Termination
              </h2>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                We may terminate or suspend your account and access to the Service immediately, without prior 
                notice, for any reason, including breach of these Terms. Upon termination, your right to use 
                the Service will cease immediately. You may also delete your account at any time through your 
                account settings.
              </p>
            </section>

            {/* Section 14 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 text-sm font-bold">14</span>
                Changes to Terms
              </h2>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                We reserve the right to modify these Terms at any time. We will notify you of material 
                changes by posting the updated Terms on our website and updating the &quot;Last Updated&quot; date. 
                Your continued use of the Service after changes become effective constitutes acceptance of 
                the revised Terms.
              </p>
            </section>

            {/* Section 15 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 text-sm font-bold">15</span>
                Governing Law
              </h2>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of the State of 
                Delaware, United States, without regard to its conflict of law provisions. Any disputes 
                arising under these Terms shall be subject to the exclusive jurisdiction of the courts 
                located in Delaware.
              </p>
            </section>

            {/* Contact Section */}
            <section className="mt-16 p-8 bg-slate-100 dark:bg-slate-800/50 rounded-2xl">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
                Contact Us
              </h2>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed mb-4">
                If you have any questions about these Terms of Service, please contact us:
              </p>
              <a 
                href="mailto:hello@easypairing.com" 
                className="inline-flex items-center gap-2 text-amber-600 dark:text-amber-400 hover:underline font-medium"
              >
                <Mail className="h-5 w-5" />
                hello@easypairing.com
              </a>
            </section>

          </div>
        </div>
      </div>
    </main>
  );
}
